import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import { PhoneFrame } from "@/components/mentor-profile/phone-frame";
import { WizardBackground } from "@/components/mentor-profile/wizard-background";
import type { MentorProfileDraft } from "@/lib/api/mentor-profile";
import { cn } from "@/lib/utils";
import { Moodies } from "@somethings-ui/core/assets";
import { Check } from "lucide-react";

interface ThanksSubmittedProps {
  draft: MentorProfileDraft;
}

const NEXT_STEPS = [
  {
    title: "Submitted",
    description: "We've got your profile.",
    state: "done" as const,
  },
  {
    title: "In review",
    description: "A teammate is reading it over.",
    state: "current" as const,
  },
  {
    title: "Approved & live",
    description: "Teens can find and message you.",
    state: "upcoming" as const,
  },
];

export function ThanksSubmitted({ draft }: ThanksSubmittedProps) {
  const name = draft.display_name || draft.first_name || "there";
  const email = draft.email?.trim();

  return (
    <WizardBackground className="items-start justify-center gap-12">
      <div className="relative z-10 flex w-full max-w-[672px] flex-col">
        <div className="flex size-14 items-center justify-center rounded-2xl bg-brand-ecru/60">
          <img
            src={Moodies.love}
            alt=""
            className="size-10 object-contain"
          />
        </div>

        <h1 className="mt-5 font-heading text-[34px] font-bold leading-[1.05] tracking-[-0.85px] text-[#1f1e1d]">
          Thanks for submitting, {name}
        </h1>

        <p className="mt-3 font-body text-[15px] leading-[1.625] text-[#797774]">
          Our team reads every profile before it goes live — usually a day or
          two. We&apos;ll email you
          {email ? (
            <>
              {" "}
              at <span className="font-medium text-[#1f1e1d]">{email}</span>
            </>
          ) : null}{" "}
          the moment there&apos;s an update. Nothing else to do right now.
        </p>

        <div className="mt-8 rounded-[22px] border border-[#ffd9c2] bg-[#fff8f4] p-[21px]">
          <p className="font-body text-xs font-semibold uppercase tracking-[0.3px] text-[#797774]">
            What happens next
          </p>
          <ol className="mt-4 flex flex-col">
            {NEXT_STEPS.map((step, index) => {
              const isLast = index === NEXT_STEPS.length - 1;
              return (
                <li key={step.title} className="flex gap-3.5">
                  <div className="flex w-7 flex-col items-center">
                    <StepMarker state={step.state} />
                    {!isLast ? (
                      <div
                        className={cn(
                          "mt-1 w-0.5 flex-1 min-h-[28px]",
                          step.state === "done" ? "bg-[#22c55e]" : "bg-[#e7e0d5]",
                        )}
                      />
                    ) : null}
                  </div>
                  <div className={cn("pb-5", isLast && "pb-0")}>
                    <p className="font-body text-[15px] font-medium text-[#1f1e1d]">
                      {step.title}
                    </p>
                    <p className="mt-0.5 font-body text-sm text-[#797774]">
                      {step.description}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      <div className="relative z-10 flex shrink-0 flex-col items-center gap-3 self-start">
        <span className="text-xs text-[#797774]">
          Live preview · what teens see
        </span>
        <PhoneFrame>
          <MatchingProfilePreviewCard draft={draft} />
        </PhoneFrame>
      </div>
    </WizardBackground>
  );
}

function StepMarker({
  state,
}: {
  state: "done" | "current" | "upcoming";
}) {
  switch (state) {
    case "done":
      return (
        <span className="flex size-7 items-center justify-center rounded-full bg-[#22c55e] text-white">
          <Check className="size-3.5" strokeWidth={2.5} />
        </span>
      );
    case "current":
      return (
        <span className="flex size-7 items-center justify-center rounded-full border-2 border-[#cfc8bd] bg-white">
          <span className="size-2 rounded-full bg-[#797774]" />
        </span>
      );
    case "upcoming":
      return (
        <span className="size-7 rounded-full border-2 border-[#e7e0d5] bg-white" />
      );
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}
